// Customers by Day — new customer signups bucketed by Bali date, plus
// a flat list underneath so the front desk can call out which guests
// registered when. Customers are brand-wide.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function CustomersByDayReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/customers-by-day`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const maxCount = Math.max(1, ...(data?.days || []).map((d) => d.count));

  const exportSpec = useMemo(() => {
    if (!data) return null;
    const rows = (data.rows || []).map((c) => ({
      registered: fmtTime(c.created_at),
      name: c.name,
      email: c.email,
      phone: c.phone,
    }));
    return {
      filename: "customers-by-day",
      range: { date_from: data.date_from, date_to: data.date_to },
      subtitle: `${data.count} signup${data.count === 1 ? "" : "s"} across ${data.days?.length || 0} day(s)`,
      columns: [
        { header: "Registered", key: "registered" },
        { header: "Name", key: "name" },
        { header: "Email", key: "email" },
        { header: "Phone", key: "phone" },
      ],
      rows,
    };
  }, [data]);

  return (
    <ReportShell title="Customers by Day" subtitle="New guest signups per Bali day. Customers are brand-wide, but the report is scoped from the location's Reports menu for consistency." branchName={branch?.name} tab="customers" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="rounded-lg overflow-hidden lg:col-span-1" data-testid="customers-by-day-summary">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-baseline justify-between">
                <h2 className="text-sm font-semibold">Signups per Day</h2>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">total</div>
                  <div className="text-lg font-semibold" data-testid="customers-by-day-total">{data?.count ?? 0}</div>
                </div>
              </div>
              <div className="p-3 space-y-1.5 max-h-96 overflow-auto">
                {(!data || data.days.length === 0) ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">{loading ? "Loading…" : "No signups in range."}</p>
                ) : data.days.map((d) => (
                  <div key={d.date} className="flex items-center gap-2 text-xs" data-testid={`customers-day-${d.date}`}>
                    <span className="w-24 shrink-0 whitespace-nowrap">{fmtDate(d.date)}</span>
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${(d.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right font-medium">{d.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg overflow-hidden lg:col-span-2" data-testid="customers-list-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold">New Guests</h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider">Registered</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Name</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Email</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Phone</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data || data.count === 0) ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">
                      {loading ? "Loading…" : "No signups in this range."}
                    </TableCell></TableRow>
                  ) : data.rows.map((c) => (
                    <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtTime(c.created_at)}</TableCell>
                      <TableCell className="text-sm">
                        <Link to={`/staff/brand/customers/${c.id}`} className="text-primary hover:underline">{c.name}</Link>
                      </TableCell>
                      <TableCell className="text-xs">{c.email}</TableCell>
                      <TableCell className="text-xs">{c.phone}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </ReportShell>
  );
}
